import React from 'react';
import { Card, CardContent, Box, Typography, alpha, useTheme, useMediaQuery } from '@mui/material';
import { LocalCafe, Star, TrendingUp, Coffee } from '@mui/icons-material';
import Grid from '@mui/material/Grid';

interface StatsSectionProps {
  stats: {
    totalCafes: number;
    avgRating: number;
    topRated: string;
    mostReviewed: number;
  };
  darkMode: boolean;
}

const StatsSection: React.FC<StatsSectionProps> = ({ stats, darkMode }) => {
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  const glassEffect = {
    background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: darkMode 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: darkMode 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  const statsData = [
    {
      title: 'Total Cafes',
      value: stats.totalCafes,
      icon: <LocalCafe sx={{ fontSize: 30 }} />,
      color: goldColor
    },
    {
      title: 'Avg Rating',
      value: stats.avgRating.toFixed(1),
      icon: <Star sx={{ fontSize: 30 }} />,
      color: goldColor
    },
    {
      title: 'Top Rated',
      // Truncate logic to prevent this card from being taller/wider than others
      value: stats.topRated.length > 12 ? stats.topRated.substring(0, 10) + '...' : (stats.topRated || '-'),
      icon: <TrendingUp sx={{ fontSize: 30 }} />,
      color: goldColor
    },
    {
      title: 'Most Reviews',
      value: stats.mostReviewed,
      icon: <Coffee sx={{ fontSize: 30 }} />,
      color: goldColor
    }
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 4, width: '100%' }}>
      {statsData.map((stat, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
          <Card sx={{ 
            borderRadius: '20px',
            ...glassEffect,
            height: '100%', // Forces all cards to match the tallest one in the row
            minHeight: '180px', // Minimum height for consistency
            width: '100%',
            minWidth: '350px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            transition: 'transform 0.3s ease',
            '&:hover': {
                transform: 'translateY(-5px)'
            }
          }}>
            <CardContent sx={{ 
              p: 3, 
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%' 
            }}>
              <Box sx={{ 
                width: 50, 
                height: 50, 
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: darkMode 
                  ? 'rgba(196, 182, 111, 0.1)' 
                  : 'rgba(5, 24, 20, 0.05)',
                mb: 2
              }}>
                {React.cloneElement(stat.icon, { sx: { color: stat.color } })}
              </Box>
              
              <Typography variant="h4" sx={{ 
                fontWeight: 800, 
                mb: 0.5, 
                color: darkMode ? '#FFFFFF' : deepGreen,
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}>
                {stat.value}
              </Typography>
              
              <Typography variant="body2" sx={{ 
                fontWeight: 600,
                color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(5, 24, 20, 0.6)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontSize: '0.75rem'
              }}>
                {stat.title}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default StatsSection;